// tab/src/components/AdminPortal/AdminPortal.tsx
// OnRounds Phase 2 - Admin Portal Component

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Card,
  Text,
  Button,
  Input,
  Tab,
  TabList,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Textarea,
  Checkbox,
  Badge,
  Avatar,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import {
  Settings24Regular,
  People24Regular,
  PersonAdd24Regular,
  ArrowUpload24Regular,
  ArrowDownload24Regular,
  Delete24Regular,
  Edit24Regular,
  Search24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { useTeam } from '../../context/TeamContext';
import { useUser } from '../../context/UserContext';
import { api } from '../../services/api';
import { Loading } from '../common/Loading';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tabs: {
    marginBottom: '16px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  searchBar: {
    display: 'flex',
    ...shorthands.gap('12px'),
    marginBottom: '16px',
  },
  tableCard: {
    ...shorthands.overflow('auto'),
  },
  table: {
    minWidth: '700px',
  },
  userActions: {
    display: 'flex',
    ...shorthands.gap('8px'),
  },
  importSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  dropZone: {
    ...shorthands.padding('40px'),
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('8px'),
    textAlign: 'center',
    cursor: 'pointer',
    ':hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  dropZoneActive: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  importResults: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px'),
  },
  resultSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  resultError: {
    color: tokens.colorPaletteRedForeground1,
  },
  roleForm: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  colorPicker: {
    display: 'flex',
    ...shorthands.gap('8px'),
    flexWrap: 'wrap',
  },
  colorOption: {
    width: '32px',
    height: '32px',
    ...shorthands.borderRadius('4px'),
    cursor: 'pointer',
    ...shorthands.border('2px', 'solid', 'transparent'),
  },
  colorOptionSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  qualificationBadge: {
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase200,
    marginRight: '4px',
  },
});

interface User {
  id: string;
  email: string;
  displayName: string;
  qualifications: string[];
  isAdmin: boolean;
  isSupervisor: boolean;
}

interface Role {
  id: string;
  name: string;
  description: string;
  category: string;
  color: string;
  requiredQualifications: string[];
  isActive: boolean;
}

interface ImportResult {
  success: boolean;
  imported: number;
  updated: number;
  errors: Array<{ row: number; email: string; error: string }>;
}

const ROLE_COLORS = [
  '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];

const ROLE_CATEGORIES = [
  { value: 'medical', label: 'Medical' },
  { value: 'nursing', label: 'Nursing' },
  { value: 'allied_health', label: 'Allied Health' },
  { value: 'admin', label: 'Administrative' },
  { value: 'support', label: 'Support' },
];

export const AdminPortal: React.FC = () => {
  const styles = useStyles();
  const { currentTeam } = useTeam();
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'import'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Import state
  const [csvContent, setCsvContent] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // User edit dialog
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Role edit dialog
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Partial<Role> | null>(null);
  const [isSavingRole, setIsSavingRole] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    if (!currentTeam) return;

    try {
      setIsLoading(true);
      setError(null);

      const [usersResult, rolesResult] = await Promise.all([
        api.getUsers(),
        api.getRoles(currentTeam.id),
      ]);

      setUsers(usersResult.users as User[]);
      setRoles(rolesResult.roles as Role[]);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter users
  const filteredUsers = users.filter((u) =>
    u.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type === 'text/csv') {
      readFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      readFile(file);
    }
  };

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setCsvContent(e.target?.result as string);
    };
    reader.readAsText(file);
  };

  // Handle import
  const handleImport = async () => {
    if (!currentTeam || !csvContent) return;

    try {
      setIsImporting(true);
      setError(null);
      setImportResult(null);

      const result = await api.importStaff({
        teamId: currentTeam.id,
        csvData: csvContent,
      } as any);

      setImportResult(result as unknown as ImportResult);
      
      if (result.success) {
        setSuccess(`Successfully imported ${result.imported} and updated ${(result as any).updated || 0} users`);
        setCsvContent('');
        loadData();
      }
    } catch (err: any) {
      console.error('Failed to import staff:', err);
      setError(err.message || 'Failed to import staff');
    } finally {
      setIsImporting(false);
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const blob = await api.downloadCSVTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'staff-import-template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to download template');
    }
  };

  // Save user
  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      await api.updateUser(editingUser.id, {
        isAdmin: editingUser.isAdmin,
        isSupervisor: editingUser.isSupervisor,
        qualifications: editingUser.qualifications,
      } as any);

      setEditUserOpen(false);
      setEditingUser(null);
      setSuccess('User updated successfully');
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    }
  };

  // Save role
  const handleSaveRole = async () => {
    if (!editingRole || !currentTeam) return;

    try {
      setIsSavingRole(true);

      if (editingRole.id) {
        // Update existing
        await api.updateRole(currentTeam.id, editingRole.id, editingRole);
      } else {
        // Create new
        await api.createRole(currentTeam.id, editingRole as any);
      }

      setEditRoleOpen(false);
      setEditingRole(null);
      setSuccess('Role saved successfully');
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save role');
    } finally {
      setIsSavingRole(false);
    }
  };

  // Delete role
  const handleDeleteRole = async (roleId: string) => {
    if (!currentTeam) return;

    if (!window.confirm('Are you sure you want to deactivate this role?')) {
      return;
    }

    try {
      await api.deleteRole(currentTeam.id, roleId);
      setSuccess('Role deactivated');
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate role');
    }
  };

  if (!user?.isAdmin) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          You don't have permission to access the admin portal.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (isLoading && users.length === 0) {
    return <Loading message="Loading admin portal..." />;
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <Text size={500} weight="semibold">Admin Portal</Text>
          <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
            Manage users, roles, and import staff
          </Text>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}
      {success && (
      <MessageBar intent="success">
          <MessageBarBody>{success}</MessageBarBody>
        </MessageBar>
      )}

      {/* Tabs */}
      <TabList
        className={styles.tabs}
        selectedValue={activeTab}
        onTabSelect={(e, data) => setActiveTab(data.value as 'users' | 'roles' | 'import')}
      >
        <Tab value="users" icon={<People24Regular />}>Users</Tab>
        <Tab value="roles" icon={<Settings24Regular />}>Roles</Tab>
        <Tab value="import" icon={<ArrowUpload24Regular />}>Import Staff</Tab>
      </TabList>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className={styles.section}>
          <div className={styles.searchBar}>
            <Input
              contentBefore={<Search24Regular />}
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e, data) => setSearchTerm(data.value)}
              style={{ flexGrow: 1, maxWidth: '400px' }}
            />
          </div>

          <Card className={styles.tableCard}>
            <Table className={styles.table}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Qualifications</TableHeaderCell>
                  <TableHeaderCell>Roles</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar name={u.displayName} size={32} />
                        <Text>{u.displayName}</Text>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Text size={200}>{u.email}</Text>
                    </TableCell>
                    <TableCell>
                      {u.qualifications.slice(0, 2).map((q) => (
                        <span key={q} className={styles.qualificationBadge}>{q}</span>
                      ))}
                      {u.qualifications.length > 2 && (
                        <span className={styles.qualificationBadge}>+{u.qualifications.length - 2}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.isAdmin && <Badge appearance="outline" color="danger" style={{ marginRight: '4px' }}>Admin</Badge>}
                      {u.isSupervisor && <Badge appearance="outline" color="warning">Supervisor</Badge>}
                    </TableCell>
                    <TableCell>
                      <Button
                        icon={<Edit24Regular />}
                        appearance="subtle"
                        size="small"
                        onClick={() => {
                          setEditingUser(u);
                          setEditUserOpen(true);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text size={400} weight="semibold">Team Roles</Text>
            <Button
              appearance="primary"
              icon={<PersonAdd24Regular />}
              onClick={() => {
                setEditingRole({
                  name: '',
                  description: '',
                  category: 'medical',
                  color: ROLE_COLORS[0],
                  requiredQualifications: [],
                  isActive: true,
                });
                setEditRoleOpen(true);
              }}
            >
              Add Role
            </Button>
          </div>

          <Card className={styles.tableCard}>
            <Table className={styles.table}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>Category</TableHeaderCell>
                  <TableHeaderCell>Required Qualifications</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: role.color,
                          }}
                        />
                        <div>
                          <Text weight="semibold">{role.name}</Text>
                          {role.description && (
                            <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                              {role.description}
                            </Text>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Text>{ROLE_CATEGORIES.find((c) => c.value === role.category)?.label || role.category}</Text>
                    </TableCell>
                    <TableCell>
                      {role.requiredQualifications.length > 0 ? (
                        role.requiredQualifications.map((q) => (
                          <span key={q} className={styles.qualificationBadge}>{q}</span>
                        ))
                      ) : (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>None</Text>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge appearance="outline" color={role.isActive ? 'success' : 'subtle'}>
                        {role.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className={styles.userActions}>
                        <Button
                          icon={<Edit24Regular />}
                          appearance="subtle"
                          size="small"
                          onClick={() => {
                            setEditingRole(role);
                            setEditRoleOpen(true);
                          }}
                        />
                        <Button
                          icon={<Delete24Regular />}
                          appearance="subtle"
                          size="small"
                          onClick={() => handleDeleteRole(role.id)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className={styles.importSection}>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text size={400} weight="semibold">Import Staff from CSV</Text>
              <Text size={200} style={{ display: 'block', marginTop: '8px', color: tokens.colorNeutralForeground3 }}>
                Upload a CSV file with staff information to bulk import users.
              </Text>

              <Button
                appearance="subtle"
                icon={<ArrowDownload24Regular />}
                onClick={handleDownloadTemplate}
                style={{ marginTop: '16px' }}
              >
                Download Template
              </Button>
            </div>

            <div
              className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <ArrowUpload24Regular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
              <Text style={{ display: 'block', marginTop: '16px' }}>
                Drag and drop a CSV file here, or click to browse
              </Text>
            </div>

            {csvContent && (
              <div style={{ padding: '16px' }}>
                <Text weight="semibold">File ready for import</Text>
                <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                  {csvContent.split('\n').length - 1} rows detected
                </Text>
                <Button
                  appearance="primary"
                  icon={isImporting ? <Spinner size="tiny" /> : <Checkmark24Regular />}
                  onClick={handleImport}
                  disabled={isImporting}
                  style={{ marginTop: '16px' }}
                >
                  {isImporting ? 'Importing...' : 'Import Staff'}
                </Button>
              </div>
            )}

            {importResult && (
              <div className={styles.importResults}>
                <Text weight="semibold">Import Results</Text>
                <div style={{ marginTop: '8px' }}>
                  <Text className={styles.resultSuccess}>
                    ✓ {importResult.imported} imported, {importResult.updated} updated
                  </Text>
                </div>
                {importResult.errors.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <Text className={styles.resultError}>
                      ✗ {importResult.errors.length} errors:
                    </Text>
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <Text key={i} size={200} style={{ display: 'block', marginLeft: '16px' }}>
                        Row {err.row}: {err.error}
                      </Text>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editUserOpen} onOpenChange={(e, data) => setEditUserOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent>
              {editingUser && (
                <div className={styles.roleForm}>
                  <Field label="Display Name">
                    <Input value={editingUser.displayName} disabled />
                  </Field>
                  <Field label="Email">
                    <Input value={editingUser.email} disabled />
                  </Field>
                  <Checkbox
                    checked={editingUser.isAdmin}
                    onChange={(e, data) => setEditingUser({ ...editingUser, isAdmin: !!data.checked })}
                    label="Administrator"
                  />
                  <Checkbox
                    checked={editingUser.isSupervisor}
                    onChange={(e, data) => setEditingUser({ ...editingUser, isSupervisor: !!data.checked })}
                    label="Supervisor"
                  />
                  <Field label="Qualifications (comma-separated)">
                    <Textarea
                      value={editingUser.qualifications.join(', ')}
                      onChange={(e, data) => setEditingUser({
                        ...editingUser,
                        qualifications: data.value.split(',').map((q) => q.trim()).filter(Boolean),
                      })}
                    />
                  </Field>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditUserOpen(false)}>Cancel</Button>
              <Button appearance="primary" onClick={handleSaveUser}>Save</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editRoleOpen} onOpenChange={(e, data) => setEditRoleOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{editingRole?.id ? 'Edit Role' : 'New Role'}</DialogTitle>
            <DialogContent>
              {editingRole && (
                <div className={styles.roleForm}>
                  <Field label="Name" required>
                    <Input
                      value={editingRole.name || ''}
                      onChange={(e, data) => setEditingRole({ ...editingRole, name: data.value })}
                    />
                  </Field>
                  <Field label="Description">
                    <Textarea
                      value={editingRole.description || ''}
                      onChange={(e, data) => setEditingRole({ ...editingRole, description: data.value })}
                    />
                  </Field>
                  <Field label="Category">
                    <select
                      value={editingRole.category || 'medical'}
                      onChange={(e) => setEditingRole({ ...editingRole, category: e.target.value })}
                      style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      {ROLE_CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Color">
                    <div className={styles.colorPicker}>
                      {ROLE_COLORS.map((color) => (
                        <div
                          key={color}
                          className={`${styles.colorOption} ${editingRole.color === color ? styles.colorOptionSelected : ''}`}
                          style={{ backgroundColor: color }}
                          onClick={() => setEditingRole({ ...editingRole, color })}
                        />
                      ))}
                    </div>
                  </Field>
                  <Field label="Required Qualifications (comma-separated)">
                    <Textarea
                      value={(editingRole.requiredQualifications || []).join(', ')}
                      onChange={(e, data) => setEditingRole({
                        ...editingRole,
                        requiredQualifications: data.value.split(',').map((q) => q.trim()).filter(Boolean),
                      })}
                    />
                  </Field>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditRoleOpen(false)}>Cancel</Button>
              <Button
                appearance="primary"
                onClick={handleSaveRole}
                disabled={isSavingRole || !editingRole?.name}
                icon={isSavingRole ? <Spinner size="tiny" /> : undefined}
              >
                {isSavingRole ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
